import { CupertinoPane } from 'cupertino-pane';
import { PaneBreaks, PaneSettings } from 'cupertino-pane/dist/types/models';
import { DebouncedFunc, debounce } from 'lodash';
import { ReactElement, useCallback, useEffect, useRef } from 'react';
import { useMeasure } from 'react-use';
import {
  useKeyboardWillHide,
  useKeyboardWillShow,
} from '../../../theme/keyboard.hook';
import { logger } from '../../../util/logger';
import { PanelBaseProps } from './content-panel';
import './panels.css';

export interface BottomPanelProps {
  enabledBottomBreak?: boolean;
}

const DISABLED_MIN_HEIGHT = 100;

type MoveToBreakFunc = (
  breakpoint: 'middle' | 'top' | 'bottom',
  paneBreaks?: PaneBreaks
) => Promise<void>;

const BottomPanel = (
  props: BottomPanelProps & PanelBaseProps
): ReactElement => {
  const {
    show,
    canScrollBackdrop,
    children,
    disableClose,
    enabledBottomBreak,
  } = props;

  const onDidDismissRef = useRef<() => Promise<void> | undefined>();
  const panelDivRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<CupertinoPane>();
  const pendingCallbackRef = useRef<(() => void) | undefined>();
  const isShowingRef = useRef<boolean>(false);
  const isKeyboardShowingRef = useRef<boolean>(false);
  const defaultPaneBreaksRef = useRef<PaneBreaks>();
  const isTransitioningRef = useRef<boolean>(false);
  const isPanelVisible = useRef<boolean>(false);

  const [childrenWrapperRef, { height: childrenHeight }] =
    useMeasure<HTMLDivElement>();

  useKeyboardWillShow(() => {
    // isKeyboardShowingRef is used to determine whether to recalculate the panel height
    // and move to that point.  if the keyboard is already showing (ie, using search before tapping on a
    // scratchpad), the initial panel isn't resized with it's content when it is being presented

    // this change only set the ref IF the panel is already presented (example: focusing an input within
    // the panel)
    if (panelRef.current?.isPanePresented() === true) {
      isKeyboardShowingRef.current = true;
    }
  });
  useKeyboardWillHide(() => (isKeyboardShowingRef.current = false));

  const destroyPanel = useCallback(async (animate = true) => {
    isShowingRef.current = false;
    if (panelRef.current?.isPanePresented() === true) {
      logger.info('bottom panel - destroying');
      await panelRef.current?.destroy({ animate });
    }
  }, []);

  const showPanel = useCallback(async (animate = true) => {
    if (panelRef.current?.isPanePresented() === false) {
      logger.info('bottom panel - showing');
      await panelRef.current?.present({ animate });
    }
    isShowingRef.current = true;
  }, []);

  useEffect(() => {
    const internalPanelDivRef = panelDivRef.current;

    return () => {
      const destroy = async () => {
        logger.info('bottom panel - unmounting');

        const animate = isTransitioningRef.current ? false : true;

        if (panelRef.current?.isPanePresented()) {
          await panelRef.current?.destroy({ animate });
        }

        // for some reason the div is never removed from the DOM so
        // we will forcefully remove it when unmounting
        internalPanelDivRef?.remove();
      };

      destroy();
    };
  }, []);

  const onClose = useCallback(
    async (cb: () => void) => {
      logger.info('bottom panel - closing');
      pendingCallbackRef.current = cb;
      await destroyPanel();
    },
    [destroyPanel]
  );

  onDidDismissRef.current = async () => {
    logger.info('bottom panel - dismissing');
    if (props.onDidDismiss) {
      await props.onDidDismiss();
    }
    if (pendingCallbackRef.current) {
      pendingCallbackRef.current();
      pendingCallbackRef.current = undefined;
    }
  };

  const createPanel = useCallback(async (settings: Partial<PaneSettings>) => {
    if (panelDivRef.current) {
      logger.info('bottom panel - creating');
      panelRef.current = new CupertinoPane(panelDivRef.current, {
        touchAngle: 60,
        parentElement: 'ion-app',
        backdrop: false,
        buttonDestroy: false,
        bottomClose: true,
        backdropOpacity: 0.4,
        ...settings,
      });
    }
  }, []);

  useEffect(() => {
    const setup = async () => {
      logger.info('bottom panel - setup');

      if (panelRef.current === undefined) {
        defaultPaneBreaksRef.current = {
          bottom: {
            enabled: true,
            height:
              disableClose || enabledBottomBreak ? DISABLED_MIN_HEIGHT : 0,
          },
        };

        await createPanel({
          bottomClose: disableClose || enabledBottomBreak ? false : true,
          initialBreak: 'bottom',
          backdrop: canScrollBackdrop !== true,
          fastSwipeClose: disableClose ? false : true,
          fastSwipeSensivity: 10,
          breaks: defaultPaneBreaksRef.current,
          events: {
            onWillPresent: () => {
              isPanelVisible.current = true;
              isTransitioningRef.current = true;
            },
            onDidPresent: () => {
              isTransitioningRef.current = false;
            },
            onWillDismiss: () => {
              isTransitioningRef.current = true;
            },
            onDidDismiss: () => {
              isPanelVisible.current = false;
              isTransitioningRef.current = false;
              if (onDidDismissRef.current) {
                onDidDismissRef.current();
              }
            },
            onBackdropTap: () => {
              if (!isTransitioningRef.current) {
                logger.info('bottom panel - backdrop tap');
                destroyPanel();
              }
            },
          },
        });
      }

      if (show) {
        if (!isShowingRef.current) {
          await destroyPanel(false);
        }

        await showPanel();
      } else {
        await destroyPanel();
      }
    };

    setup();
  }, [
    show,
    showPanel,
    destroyPanel,
    disableClose,
    createPanel,
    canScrollBackdrop,
    enabledBottomBreak,
  ]);

  const moveToBreakRef = useRef<DebouncedFunc<MoveToBreakFunc>>();
  const moveToBreak = useCallback<MoveToBreakFunc>(
    async (
      breakpoint: 'middle' | 'top' | 'bottom',
      paneBreaks?: PaneBreaks
    ) => {
      const canMove = panelRef.current?.isPanePresented();

      logger.info(
        'bottom panel - moving to break - ' + canMove + ' - ' + breakpoint
      );

      if (canMove) {
        if (paneBreaks) {
          logger.info('bottom panel - setting new pane breaks');
          await panelRef.current?.setBreakpoints(paneBreaks);
        }

        panelRef.current?.moveToBreak(breakpoint);
      }
    },
    []
  );

  useEffect(() => {
    moveToBreakRef.current = debounce(moveToBreak, 100);
  }, [moveToBreak]);

  useEffect(() => {
    // default values used internally by cupertino
    if (!isKeyboardShowingRef.current) {
      const defaultTopHeight =
        (panelRef.current?.screen_height ?? window.innerHeight) - 135 * 0.35;
      const defaultMidHeight = 300;

      const midHeight = childrenHeight;

      const existingPaneBreaks: PaneBreaks = {
        ...(defaultPaneBreaksRef.current ?? {}),
      };

      let breakpoint: 'middle' | 'top' = 'middle';
      let paneBreaks: PaneBreaks | undefined;

      // subtract 50 pixels since midpoint won't be enabled if within 50 pixels
      // of the top point
      const maxHeight = defaultTopHeight - 50;

      logger.info(
        `bottom panel - defaultTopHeight=${defaultTopHeight}, maxHeight=${maxHeight}, midHeight=${midHeight}`
      );

      if (maxHeight <= midHeight) {
        logger.info('bottom panel - using default top breakpoint');
        breakpoint = 'top';
        paneBreaks = {
          ...existingPaneBreaks,
          top: {
            enabled: true,
            height: defaultTopHeight,
          },
          middle: {
            enabled: true,
            height: defaultMidHeight,
          },
        };
      } else {
        const paneEl = panelDivRef.current?.parentElement;
        // topOffset is the amount the content is offset from the top of the panel
        let topOffset = 0;
        if (paneEl) {
          const wrapperEl = paneEl.getElementsByClassName(
            'bottom-panel-wrapper'
          )[0];
          topOffset =
            wrapperEl.getBoundingClientRect().top -
            paneEl.getBoundingClientRect().top;
        }

        const finalMidHeight =
          Math.max(
            Math.min(midHeight, defaultTopHeight),
            (existingPaneBreaks?.bottom?.height ?? 0) + 1
          ) + topOffset;

        logger.info(
          `bottom panel - using midpoint - finalMidHeight=${finalMidHeight}`
        );

        breakpoint = 'middle';
        paneBreaks = {
          ...existingPaneBreaks,
          top: {
            enabled: true,
            height: defaultTopHeight,
          },
          middle: {
            enabled: true,
            height: finalMidHeight,
          },
        };
      }

      if (moveToBreakRef.current) {
        moveToBreakRef.current(breakpoint, paneBreaks);
      }
    }
  }, [childrenHeight]);

  return (
    <div className="bottom-panel-wrapper" ref={panelDivRef}>
      <div ref={childrenWrapperRef}>
        <div className="bottom-panel-content">
          {isPanelVisible.current &&
            (typeof children === 'function'
              ? children({ onClose, dismiss: destroyPanel })
              : children)}
        </div>
      </div>
    </div>
  );
};

export { BottomPanel };
